import React, { useEffect, useRef, useState } from "react";
import { Search, X, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Zoek-selector die uit /api/listings kiest.
 * onSelect(listing | null)
 */
export const ListingSearchSelect = ({ onSelect, selected }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(`${API}/listings`, {
          params: { q, limit: 8 },
          signal: controller.signal,
        });
        if (!cancelled) setResults(res.data.items || []);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (selected) {
    return (
      <div
        data-testid="listing-selected"
        className="flex items-center justify-between gap-3 bg-[#F4F8FB] border border-[#6BC0E8] rounded-xl px-4 py-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 rounded-full bg-[#2E9CD6] text-white flex items-center justify-center shrink-0">
            <Check className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="font-[Poppins] font-semibold text-[#173A5E] truncate">
              {selected.name}
            </p>
            <p className="text-xs text-[#5A7594] truncate">
              {[selected.address, selected.city].filter(Boolean).join(", ") ||
                "Adres niet bekend"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSelect(null)}
          data-testid="listing-select-clear"
          className="p-2 rounded-full text-[#5A7594] hover:text-[#CE1B2A] hover:bg-white transition"
          aria-label="Selectie wissen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="bg-white rounded-xl border border-[#E4EEF5] flex items-center px-4 py-2.5 focus-within:border-[#2E9CD6] focus-within:ring-2 focus-within:ring-[#6BC0E8]/40 transition">
        <Search className="w-5 h-5 text-[#2E9CD6] shrink-0" />
        <input
          data-testid="listing-select-search"
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Zoek je bedrijf op naam..."
          className="flex-1 px-3 py-1 bg-transparent outline-none font-[Inter] text-[15px] text-[#173A5E] placeholder:text-[#8AA3B8]"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="p-1 text-[#8AA3B8] hover:text-[#173A5E]"
            aria-label="Wissen"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div
          data-testid="listing-select-dropdown"
          className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#E4EEF5] rounded-xl shadow-xl overflow-hidden max-h-80 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-sm text-[#5A7594]">Zoeken…</div>
          ) : results.length === 0 ? (
            <div className="p-4 text-sm text-[#5A7594]">
              Geen bedrijven gevonden. Staat je bedrijf er nog niet op?{" "}
              <span className="text-[#2E9CD6]">
                Gebruik dan het formulier hierboven om je aan te melden.
              </span>
            </div>
          ) : (
            <ul>
              {results.map((r) => (
                <li key={r.slug}>
                  <button
                    type="button"
                    data-testid={`listing-select-option-${r.slug}`}
                    onClick={() => {
                      onSelect(r);
                      setOpen(false);
                      setQ("");
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-[#F4F8FB] flex items-center justify-between gap-3 transition"
                  >
                    <div className="min-w-0">
                      <p className="font-[Poppins] font-semibold text-[#173A5E] truncate">
                        {r.name}
                      </p>
                      <p className="text-xs text-[#5A7594] truncate">
                        {[r.address, r.city].filter(Boolean).join(", ") ||
                          "Adres niet bekend"}
                      </p>
                    </div>
                    <span className="text-xs text-[#2E9CD6] font-[Poppins] font-semibold shrink-0">
                      Kies
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingSearchSelect;
